import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, Files, Loader2, X } from 'lucide-react';
import { propsFundoModal } from '../../src/lib/backdropModal';
import { useAuth } from '../../src/contexts/AuthContext';
import { useToast } from '../../src/contexts/ToastContext';
import { logAction, logUsageEvent } from '../../src/lib/logger';
import { mesReferenciaValido, motivoDuplicataValido, MOTIVO_MINIMO } from '../../src/lib/duplicataFatura';
import { autorizarDuplicata, buscarFaturasAtivasDoMes, type FaturaDoMes } from './duplicataFaturaApi';

interface ConfirmarDuplicataModalProps {
  /** UC e mês já conhecidos (formulário manual ou mensagem do erro). Vazio = a tela pergunta. */
  ucInicial?: string;
  mesInicial?: string;
  origem: 'manual' | 'upload_pdf';
  onClose: () => void;
  /** Chamado depois de autorizar: quem abriu refaz o que estava fazendo (salvar ou reenviar o PDF). */
  onAutorizado: (dados: { uc: string; mes: string; motivo: string }) => void;
}

const formatarValor = (v: number | string | null) => {
  const n = Number(v);
  return Number.isFinite(n) && n !== 0 ? n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : '—';
};

/**
 * Pergunta se a segunda fatura do mesmo mês deve mesmo ficar, e registra o motivo.
 * Nada é inserido aqui: a autorização é que libera o próximo INSERT no banco.
 */
const ConfirmarDuplicataModal: React.FC<ConfirmarDuplicataModalProps> = ({ ucInicial, mesInicial, origem, onClose, onAutorizado }) => {
  const { user, profile } = useAuth();
  const { addToast } = useToast();
  const [uc, setUc] = useState(ucInicial || '');
  const [mes, setMes] = useState((mesInicial || '').toUpperCase());
  const [motivo, setMotivo] = useState('');
  const [existentes, setExistentes] = useState<FaturaDoMes[] | null>(null);
  const [buscando, setBuscando] = useState(false);
  const [salvando, setSalvando] = useState(false);

  const podeBuscar = uc.trim().length >= 3 && mesReferenciaValido(mes);

  useEffect(() => {
    if (!podeBuscar) {
      setExistentes(null);
      return;
    }
    let ativo = true;
    setBuscando(true);
    buscarFaturasAtivasDoMes(uc, mes)
      .then(lista => { if (ativo) setExistentes(lista); })
      .catch(err => {
        console.error('Erro ao buscar faturas do mês', err);
        if (ativo) setExistentes([]);
      })
      .finally(() => { if (ativo) setBuscando(false); });
    return () => { ativo = false; };
  }, [uc, mes, podeBuscar]);

  const erro = !uc.trim()
    ? 'Informe a UC.'
    : !mesReferenciaValido(mes)
      ? 'Informe o mês de referência no formato SET/2026.'
      : !motivoDuplicataValido(motivo)
        ? `Escreva o motivo (mínimo ${MOTIVO_MINIMO} caracteres).`
        : null;

  const confirmar = async () => {
    if (erro || salvando) return;
    setSalvando(true);
    try {
      await autorizarDuplicata({
        uc,
        mes,
        motivo,
        origem,
        userId: user?.id ?? null,
        nome: profile?.full_name ?? null,
      });
      await logAction(user?.id, 'AUTORIZAR_FATURA_DUPLICADA', { uc: uc.trim(), mes_referencia: mes.trim().toUpperCase(), motivo: motivo.trim(), origem });
      logUsageEvent({ userId: user?.id, modulo: 'financeiro', funcionalidade: 'faturas', acao: 'autorizar_fatura_duplicada', origem: 'acao' });
      onAutorizado({ uc: uc.trim(), mes: mes.trim().toUpperCase(), motivo: motivo.trim() });
    } catch (e: any) {
      console.error('Erro ao autorizar duplicata', e);
      addToast('Não foi possível autorizar a duplicata: ' + (e?.message || 'erro desconhecido'), 'error');
      setSalvando(false);
    }
  };

  const campo = 'w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm text-gray-800 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent';

  return createPortal(
    <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in" {...propsFundoModal(onClose)}>
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] flex flex-col overflow-hidden animate-slide-up" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 p-5 border-b border-gray-100 dark:border-gray-700 bg-amber-50/60 dark:bg-amber-900/10">
          <div className="flex gap-3">
            <div className="p-2.5 bg-amber-100 dark:bg-amber-900/30 text-amber-600 rounded-xl h-fit">
              <Files size={20} />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900 dark:text-white font-poppins">Já existe fatura deste mês para esta UC</h2>
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                A distribuidora às vezes emite duas faturas no mesmo mês (leitura residual, por exemplo). Se a segunda é legítima e deve ser cobrada, confirme aqui e diga o porquê.
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full" aria-label="Fechar">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-4 overflow-y-auto">
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs font-semibold text-gray-600 dark:text-gray-300">UC</span>
              <input value={uc} onChange={e => setUc(e.target.value)} className={`${campo} mt-1 tabular-nums`} placeholder="348368701220" readOnly={!!ucInicial} />
            </label>
            <label className="block">
              <span className="text-xs font-semibold text-gray-600 dark:text-gray-300">Mês de referência</span>
              <input
                value={mes}
                onChange={e => setMes(e.target.value.toUpperCase())}
                className={`${campo} mt-1`}
                placeholder="SET/2026"
                readOnly={!!mesInicial}
              />
            </label>
          </div>

          <div className="rounded-xl border border-gray-100 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-900/30 p-3">
            <p className="text-[11px] font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
              Faturas que já estão no sistema neste mês
            </p>
            {buscando ? (
              <p className="flex items-center gap-2 text-xs text-gray-400"><Loader2 size={13} className="animate-spin" /> Procurando…</p>
            ) : !existentes ? (
              <p className="text-xs text-gray-400">Informe a UC e o mês para ver.</p>
            ) : existentes.length === 0 ? (
              <p className="text-xs text-gray-400">Nenhuma fatura ativa encontrada — talvez não seja duplicata.</p>
            ) : (
              <ul className="space-y-1.5">
                {existentes.map(f => (
                  <li key={f.id} className="flex flex-wrap items-center justify-between gap-2 text-xs text-gray-700 dark:text-gray-200">
                    <span className="font-semibold">
                      {f.cliente_nome || 'Sem nome'} <span className="font-normal text-gray-400">#{f.id}</span>
                    </span>
                    <span className="flex flex-wrap gap-3 text-gray-500 dark:text-gray-400 tabular-nums">
                      <span>{f.status_fatura || '—'}</span>
                      <span>{formatarValor(f.valor_fatura_distribuidora)}</span>
                      <span>chegou em {new Date(f.created_at).toLocaleDateString('pt-BR')}</span>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <label className="block">
            <span className="text-xs font-semibold text-gray-600 dark:text-gray-300">Por que esta segunda fatura deve ser mantida e cobrada?</span>
            <textarea
              value={motivo}
              onChange={e => setMotivo(e.target.value)}
              rows={3}
              className={`${campo} mt-1 resize-none`}
              placeholder="Ex.: leitura residual — a Equatorial emitiu duas faturas no mês, as duas são devidas."
            />
            {!motivoDuplicataValido(motivo) ? (
              <span className="text-[11px] text-gray-400 tabular-nums">
                Faltam {Math.max(0, MOTIVO_MINIMO - motivo.trim().length)} caracteres.
              </span>
            ) : null}
          </label>

          <div className="flex gap-2 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/50 text-[11px] text-amber-800 dark:text-amber-300">
            <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
            <span>
              A autorização vale para <strong>uma</strong> fatura desta UC neste mês e por 1 dia. As duas faturas ficam marcadas como duplicata legítima, seguem para cálculo e podem ser cobradas normalmente.
            </span>
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 p-4 border-t border-gray-100 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-900/30">
          <p className="text-[11px] text-red-600 dark:text-red-400 min-h-[1rem]">{erro || ''}</p>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50">
              Cancelar
            </button>
            <button
              onClick={confirmar}
              disabled={!!erro || salvando}
              className="px-4 py-2 text-sm font-bold text-white bg-primary rounded-lg hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
            >
              {salvando ? <Loader2 size={14} className="animate-spin" /> : null}
              Manter as duas e faturar
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ConfirmarDuplicataModal;
